import { queryKeys } from '@core/Data/ReactQuery/constants'
import { useQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { selectPlayerSessionId } from '@core/Areas/KambiClient/Store/selectors'
import { useFeature } from '@core/Utils/hooks/useFeature'
import { getBetLoopCanShareTickets } from '@core/Areas/BetLoop/helpers/api'
import { getKambiPendingBetHistory } from '@core/Areas/KambiSports/helpers/api/player'
import type { KambiHistoryCoupon } from '@mobi/api-types/kambi'

export const useKambiSharePendingTicketsData = () => {
  const isBetLoopSportActive = useFeature('bet-loop-sports')
  const kambiSessionToken = useSelector(selectPlayerSessionId)

  const { isLoading, data, isError } = useQuery({
    queryKey: [queryKeys.betLoopSharePendingTickets, 'kambi'],
    queryFn: () => fetchTicketsData(),
    staleTime: 0,
    refetchOnMount: 'always',
    enabled: isBetLoopSportActive && !!kambiSessionToken,
  })

  const kambiTickets = data?.data || []
  const isEmptyState = !isLoading && kambiTickets.length === 0

  return {
    kambiTickets,
    isError,
    isLoading,
    isEmptyState
  }
}

// =============
// Local Helpers
// =============

const fetchTicketsData = async (): Promise<FetchPendingTicketsData> => {
  const pendingKambiTicketsData = await getKambiPendingBetHistory()

  const kambiTickets: KambiHistoryCoupon[] = pendingKambiTicketsData?.historyCoupons ?? []

  if (kambiTickets.length === 0) return { data: [] }

  const canSharePayload = buildCanShareRequestPayload(kambiTickets)
  const canShareData = await getBetLoopCanShareTickets([], canSharePayload.betRefPairs)

  const shareableKambiBets = canShareData.flatMap(ticket =>
    !ticket.canShare || !ticket.betRef ? [] : [ticket.betRef]
  )

  if (shareableKambiBets.length === 0) return { data: [] }

  const filteredKambiTickets = kambiTickets.filter(coupon =>
    coupon.bets.some(bet => shareableKambiBets.includes(bet.betRef))
  )

  return { data: filteredKambiTickets }
}

const buildCanShareRequestPayload = (
  kambiTickets: KambiHistoryCoupon[]
): CanShareRequestPayload => {
  return {
    betRefPairs: kambiTickets.flatMap(coupon =>
      coupon.bets.map(bet => ({
        couponRef: coupon.couponRef,
        betRef: bet.betRef,
      }))
    ),
  }
}

// =====
// Types
// =====

type FetchPendingTicketsData = {
  data: KambiHistoryCoupon[]
}

interface CanShareRequestPayload {
  betRefPairs: { couponRef: number; betRef: number }[]
}
